import { createSlice } from '@reduxjs/toolkit'
import { RootState } from 'store'
import { searchGames } from './thunks'
import { deleteGame, deleteGamePlayer, deleteGameRound, updateGame } from 'store/game/thunks'

const initialState: GamesState = {
  games: [],
  searchLimit: 50,
  searchOffset: 0,
  status: 'idle',
  error: null,
}

const slice = createSlice({
  name: 'games',
  initialState,
  reducers: {
    clearGames: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchGames.pending, (state, action) => {
        const { offset } = action.meta.arg
        state.status = 'loading'
        state.error = null
        if (offset === 0) {
          state.games = []
        }
      })
      .addCase(searchGames.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(searchGames.fulfilled, (state, action) => {
        const { limit, offset } = action.meta.arg
        const data = action.payload
        state.status = 'succeeded'
        state.games = offset > 0 ? state.games.concat(data) : data
        state.searchOffset = offset + limit
      })

    builder.addCase(updateGame.fulfilled, (state, action) => {
      const { data } = action.meta.arg

      if (data.rounds.length > 0) {
        const roundId = data.rounds[0].id ? Number(data.rounds[0].id) : 0
        const roundIndex = state.games.map((round: GameSearchRound) => round.roundId).indexOf(roundId)
        const indexIsValid = [undefined, -1].includes(roundIndex) === false
        if (indexIsValid) {
          state.games[roundIndex].isCompleted = data.rounds[0].isCompleted || false
        }
      }
    })

    builder.addCase(deleteGame.fulfilled, (state, action) => {
      const gameId = typeof action.meta.arg === 'string' ? parseInt(action.meta.arg) : action.meta.arg
      const currentIndex = state.games.map((round: GameSearchRound) => round.eventId).indexOf(gameId)
      const indexIsValid = [undefined, -1].includes(currentIndex) === false
      if (indexIsValid) {
        state.games.splice(currentIndex, 1)
      }
    })

    builder.addCase(deleteGameRound.fulfilled, (state, action) => {
      const roundId = action.meta.arg.roundId
      const currentIndex = state.games.map((round: GameSearchRound) => round.roundId).indexOf(roundId)
      const indexIsValid = [undefined, -1].includes(currentIndex) === false
      if (indexIsValid) {
        state.games.splice(currentIndex, 1)
      }
    })

    builder.addCase(deleteGamePlayer.fulfilled, (state, action) => {
      const { gameId, playerId } = action.meta.arg
      const gameIndex = state.games.map((round: GameSearchRound) => round.eventId).indexOf(gameId)

      const indexIsValid = [undefined, -1].includes(gameIndex) === false
      if (indexIsValid) {
        const playerIndex = state.games[gameIndex].players
          .map((player: RoundPlayer) => player.profileId)
          .indexOf(playerId)

        if (playerIndex !== undefined) {
          state.games[gameIndex].players.splice(playerIndex, 1)
        }
      }
    })
  },
})

export const { clearGames } = slice.actions

export const selectGames = (state: RootState): GamesState => state.games

export default slice.reducer
